import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { PlanInfo, CurrentPlan, BillingUsage, BillingResponse, TrialSummary, RiskSummary, UpgradePlanRequest } from '../types';
import toast from 'react-hot-toast';

// Get all available plans (public endpoint)
export const usePlans = () => {
    return useQuery<PlanInfo[]>({
        queryKey: ['plans'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: PlanInfo[];
            }>('/plans');
            return data.data;
        },
    });
};

// Get current plan
export const useCurrentPlan = () => {
    return useQuery<CurrentPlan>({
        queryKey: ['billing-current'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: CurrentPlan;
            }>('/billing/current');
            return data.data;
        },
    });
};

// Get usage statistics
export const useBillingUsage = () => {
    return useQuery<BillingUsage>({
        queryKey: ['billing-usage'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: BillingUsage;
            }>('/billing/usage');
            return data.data;
        },
    });
};

// Get billing information (includes available plans)
export const useBilling = () => {
    return useQuery<BillingResponse>({
        queryKey: ['organization-billing'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: BillingResponse;
            }>('/organization/billing');
            return data.data;
        },
    });
};

// Get risk summary (replaces trial summary)
export const useRiskSummary = (enabled: boolean = true) => {
    return useQuery<RiskSummary>({
        queryKey: ['risk-summary'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: RiskSummary;
            }>('/organization/risk-summary');
            return data.data;
        },
        retry: false,
        enabled,
    });
};

// DEPRECATED: Get trial summary (kept for backward compatibility)
// Use useRiskSummary instead
export const useTrialSummary = (enabled: boolean = true) => {
    return useQuery<TrialSummary>({
        queryKey: ['trial-summary'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: TrialSummary;
            }>('/organization/trial-summary');
            return data.data;
        },
        retry: false, // Don't retry if not on trial
        enabled, // Only fetch when enabled
    });
};

// Upgrade plan
export const useUpgradePlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: UpgradePlanRequest) => {
            const { data } = await api.post<{
                success: boolean;
                message: string;
                data: {
                    plan: string;
                    plan_name: string;
                    price: number;
                };
            }>('/billing/upgrade', request);
            return data;
        },
        onSuccess: (data: any) => {
            queryClient.invalidateQueries({ queryKey: ['billing-current'] });
            queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
            queryClient.invalidateQueries({ queryKey: ['organization-billing'] });
            queryClient.invalidateQueries({ queryKey: ['organization'] });
            queryClient.invalidateQueries({ queryKey: ['ai-settings'] }); // AI availability may change
            toast.success(data.message || 'Plan upgraded successfully! 🎉');
        },
        onError: (error: any) => {
            const errorCode = error.response?.data?.error?.code;
            const errorMessage = error.response?.data?.error?.message;

            if (errorCode === 'DOWNGRADE_NOT_ALLOWED') {
                toast.error(errorMessage || 'Plan downgrade is not allowed. Please contact support.');
            } else if (errorCode === 'TEAM_SIZE_EXCEEDED') {
                toast.error(errorMessage || 'Your team size exceeds the limit for this plan.');
            } else if (errorCode === 'PAYMENT_LIMIT_EXCEEDED') {
                toast.error(errorMessage || 'Your payment limit has been exceeded.');
            } else if (errorCode === 'TRIAL_EXPIRED') {
                toast.error(errorMessage || 'Your trial has expired. Please upgrade to continue.');
            } else {
                toast.error(errorMessage || 'Failed to upgrade plan. Please try again.');
            }
        },
    });
};
